// Global Components
import * as React from 'react';
import {
  IoCheckmarkCircleOutline,
} from 'react-icons/io5';

// Helpers
import _ from 'lodash';
import {
  isDiff,
} from '@vroskus/library-helpers/dist/browser';

// Components
import InputLabel from '../InputLabel';
import InputText from '../InputText';
import InputHidden from '../InputHidden';

// Types
type $OptionalProps = {
  className?: string;
  disabled?: boolean;
  entryId?: null | string;
  label?: string;
  labelClassName?: string;
  onChange?: (value: null | string) => unknown;
  placeholder?: string;
  required?: boolean;
  unique?: (value: string) => Promise<boolean>;
  value?: null | string;
};

type $Props = $OptionalProps & {
  highlight: boolean;
  name: string;
};

type $State = {
  isUnique: boolean;
  value: null | string;
  value1: string;
  value2: string;
};

type $DisassembleValue = (value: string) => {
  value1: string;
  value2: string;
};

const disassembleValue: $DisassembleValue = (value) => {
  const parts = value.split('-');

  const value1: string = _.get(
    parts,
    '[0]',
    '',
  );

  const value2: string = _.get(
    parts,
    '[1]',
    '',
  );

  return {
    value1,
    value2,
  };
};

type $AssembleValue = (arg0: {
  value1: string;
  value2: string;
}) => null | string;

const assembleValue: $AssembleValue = ({
  value1,
  value2,
}) => {
  if (value1 === '' && value2 === '') {
    return null;
  }

  let value = `${value1}-${value2}`;

  value = value.toUpperCase();

  return value;
};

class InputNoView extends React.Component<$Props, $State> {
  static defaultProps: $OptionalProps = {
    className: undefined,
    disabled: undefined,
    entryId: undefined,
    label: undefined,
    labelClassName: undefined,
    onChange: undefined,
    placeholder: undefined,
    required: undefined,
    unique: undefined,
    value: undefined,
  };

  constructor(props: $Props) {
    super(props);

    this.state = {
      isUnique: false,
      value: null,
      value1: '',
      value2: '',
    };
  }

  componentDidMount(): void {
    this.setInitialValue();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      value,
    } = this.props;

    if (isDiff(
      prevProps.value,
      value,
    )) {
      this.setInitialValue();
    }
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  setInitialValue(): void {
    const {
      value,
    } = this.props;

    let params = {
      value1: '',
      value2: '',
    };

    if (value) {
      params = disassembleValue(value);
    }

    this.setValue(params);
  }

  async setValue({
    value1,
    value2,
  }: {
    value1: string;
    value2: string;
  }): Promise<void> {
    const {
      onChange,
      unique,
    } = this.props;

    const value = assembleValue({
      value1,
      value2,
    });

    let isUnique = true;

    if (unique && value !== null) {
      isUnique = await unique(value);
    }

    await this.setStateAsync({
      isUnique,
      value,
      value1,
      value2,
    });

    if (onChange) {
      onChange(value);
    }
  }

  renderInput() {
    const {
      disabled,
      entryId,
      highlight,
      name,
      placeholder,
      required,
      unique,
    } = this.props;
    const {
      value,
      value1,
      value2,
    } = this.state;

    const validations = unique ? {
      unique: () => {
        const {
          isUnique,
        } = this.state;

        return isUnique || 'Not unique';
      },
    } : undefined;

    return (
      <>
        <div className={'flex flex-row'}>
          <div className={'w-24 mr-2'}>
            <InputText
              disabled={disabled}
              highlight={highlight}
              name={`_1${name}`}
              onChange={(newValue) => this.setValue({
                value1: newValue,
                value2,
              })}
              placeholder={placeholder}
              required={required}
              validations={validations}
              value={value1}
            />
          </div>
          <div className={'flex-grow relative'}>
            <InputText
              disabled={disabled}
              highlight={highlight}
              name={`_2${name}`}
              onChange={(newValue) => this.setValue({
                value1,
                value2: newValue,
              })}
              required={required}
              validations={validations}
              value={value2}
            />
            {entryId && (
              <IoCheckmarkCircleOutline
                className={'absolute right-0 top-0 bottom-0 mr-2 mt-2 w-6 h-6 text-green-500'}
              />
            )}
          </div>
        </div>
        <InputHidden
          name={name}
          value={value}
        />
      </>
    );
  }

  render() {
    const {
      className,
      label,
      labelClassName,
      name,
      required,
    } = this.props;

    return (
      <div className={`InputNo ${className || ''}`}>
        <InputLabel
          className={labelClassName}
          label={label}
          name={name}
          required={required}
        />
        {this.renderInput()}
      </div>
    );
  }
}

export default InputNoView;
